import Phaser from 'phaser';
import { coinLabel } from '../../config/coinLabel';
import { HUB_CHAPTERS, isHubLevelUnlocked, type HubChapterDef } from '../../config/hubLevels';
import { HUB_MENU_TRACK } from '../../config/levels';
import type { EnemyType } from '../../core/types';
import { ENEMY_ALPHA } from '../EnemyView';
import { MARKET_TOWERS, SHOP_PRICES, type ShopTowerType } from '../../config/unlocks';
import { TOWER_NAMES, TOWER_TUTORIALS } from '../../config/displayNames';
import type { AchievementId } from '../../config/achievements';
import {
  potionShopUnlocked,
  POTION_LOCK_HINT,
  marketUnlocked,
  MARKET_LOCK_TEXT,
  MARKET_LOCK_ART,
} from '../../config/hub';
import {
  POTIONS,
  POTION_SLOTS,
  cycleIndex,
  cycleEndsAt,
  rollStock,
  soldFor,
  type PotionKind,
} from '../../config/potions';
import { formatRemaining } from '../../config/effects';
import {
  formatQuestCountdown,
  periodEndsAt,
  periodIndex,
  QUEST_REWARD,
  type QuestPeriod,
  type QuestRow,
} from '../../config/quests';
import { UPDATES, updateStatus, updateCountdown, type UpdateEntry } from '../../config/updates';
import { ARCHIVED_EVENTS, type ArchivedEvent } from '../../config/archive';
import { ProgressService } from '../../services/progressService';
import { RankService } from '../../services/rankService';
import { saveStorage } from '../saveStorage';
import { drawStarfield } from '../starfield';
import { DARKCOIN_SPIN_FRAMES, LIGHTCOIN_SPIN_FRAMES, spinCoin } from '../coin';
import { ORIGIN_CHAPTERS, type OriginChapterDef } from '../../config/originEvent';
import { getAudioService } from '../audio/AudioService';
import { getMusicService } from '../audio/MusicService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';
import { sweepAndToastAchievements, enqueueAchievementToast } from '../../ui/achievementToast';
import { potionEpoch } from '../../account/potionRestock';
import { quests, recordStat } from '../quests';
import {
  GOLD,
  GOLD_HEX,
  HUB_BLUE,
  HUB_BLUE_HEX,
  HUB_RED,
  HUB_RED_HEX,
  hubProgress,
  showUseDialog,
  usePotion,
  flashHint,
} from '../hubShared';

export type HubTab = 'levels' | 'quests' | 'market' | 'potions' | 'events' | 'explore';

/** The EXPLORE tab's accent (the Level Creator). */
const EXPLORE_TEAL = 0x2fb8a6;
const EXPLORE_TEAL_HEX = '#2fb8a6';

const ROW_W = 880;
const ROW_H = 92;
const SLOT_H = 110;
const AREA_TOP = 140;
/** Potion shop row centres — the Shop's spacing, shifted for the tab bar. */
const POTION_ROW_YS = [232, 382, 532];
const POTION_ROW_H = 130;
/** Market row centres (three Hambis since Revision 7) and height. */
const MARKET_ROW_YS = [262, 412, 562];
const MARKET_ROW_H = 130;
/** The Update Log dialog's depth (above every tab body). */
const LOG_D = 100;
/** Quest rows (Revision 9): three panels, a bar each, the countdown below. */
const QUEST_ROW_YS = [290, 400, 510];
const QUEST_ROW_H = 96;
const QUEST_BAR_W = 520;
const QUEST_ORANGE = 0xf0a04b;
const QUEST_ORANGE_HEX = '#f0a04b';
const QUEST_GREEN = 0x4caf50;

/**
 * HubScene — The Hub's tabbed menu: LEVELS (the hub chapters), QUESTS (the
 * daily/weekly quests), MARKET, POTIONS (the Potion Shop) and EVENTS (the
 * Origin Event, the Update Log and the Event Archive). Reached from the red THE HUB button on the title screen; BACK
 * returns there.
 * Spec: docs/superpowers/specs/2026-09-02-the-hub-design.md; quests and the
 * two new potions: docs/superpowers/specs/2026-09-08-quests-and-potions-design.md.
 */
/** The Origin Event's two portrait cards: left (Above the Clouds), right (The Dark Shadows). */
const ORIGIN_CARD_XS = [470, 810];
/** Card size — taller than wide, the cover art's 2:3 — and centre height. */
const ORIGIN_CARD = { w: 240, h: 360, y: 405 };
/**
 * The EVENTS tab's two decorative ghosts (2026-10-06), in the corners the
 * cards (x 350–930, y 225–585) leave empty. Both sprites face LEFT, so the
 * bottom-left Ghost is flipped to look in at the cards like the Dark Ghost.
 */
const EVENT_GHOSTS: {
  name: string;
  type: EnemyType;
  key: string;
  x: number;
  y: number;
  size: number;
  flip: boolean;
  bobMs: number;
}[] = [
  {
    name: 'hub-events-ghost',
    type: 'ghost',
    key: 'enemy-ghost',
    x: 175,
    y: 600,
    size: 150,
    flip: true,
    bobMs: 1700,
  },
  {
    name: 'hub-events-darkghost',
    type: 'darkghost',
    key: 'enemy-darkghost',
    x: 1110,
    y: 565,
    size: 250,
    flip: false,
    bobMs: 2100,
  },
];
/** The Event Archive's star: the Update Log's square, in light blue. */
const ARCHIVE_BLUE = 0x7fd3ff;
const ARCHIVE_BLUE_HOVER = 0xa8e2ff;
const ARCHIVE_BLUE_HEX = '#7fd3ff';

export class HubScene extends Phaser.Scene {
  private progress!: ProgressService;
  private rank!: RankService;
  private tab: HubTab = 'levels';
  private requestedTab?: HubTab;
  private body?: Phaser.GameObjects.Container;
  private tabPills: { tab: HubTab; txt: Phaser.GameObjects.Text }[] = [];
  private balanceText!: Phaser.GameObjects.Text;
  private backdrop?: Phaser.GameObjects.Image;
  private starfield?: Phaser.GameObjects.Container;
  private dim?: Phaser.GameObjects.Rectangle;
  /** The Potions tab's live countdown and its cycle, so a rollover redraws. */
  private countdown?: Phaser.GameObjects.Text;
  private shownCycle = -1;
  /** The Quests tab: which list is showing, its period index, its countdown. */
  private questPeriod: QuestPeriod = 'daily';
  private questIndex = -1;
  private questCountdown?: Phaser.GameObjects.Text;
  private busy = false;
  /** Reopen the Event Archive on arrival (BACK TO ARCHIVE from a run). */
  private openArchiveOnCreate = false;
  /** The open Update Log's live pieces (its countdown ticks in `update`). */
  private logTimer?: {
    entry: UpdateEntry;
    countdown: Phaser.GameObjects.Text;
    status: Phaser.GameObjects.Text;
    last: string;
  };

  constructor() {
    super({ key: 'HubScene' });
  }

  init(data: { tab?: HubTab; period?: QuestPeriod; archive?: boolean }): void {
    this.requestedTab = data?.tab;
    this.questPeriod = data?.period ?? 'daily';
    this.openArchiveOnCreate = data?.archive === true;
    // One-shot: Phaser hands a scene's last start data to the next
    // `scene.start('HubScene')` that passes none (the menu's THE HUB button),
    // which would reopen the archive on an ordinary visit.
    if (this.openArchiveOnCreate) this.sys.settings.data = { ...data, archive: false };
  }

  create(): void {
    const cx = 640;
    this.progress = hubProgress();
    // The Capybara Event closed (2026-10-06): a capsule still on order has no
    // OPEN / STORE button any more, so it goes onto the Storage shelf.
    this.progress.shelvePendingCapsule();
    this.rank = new RankService(saveStorage());
    this.body = undefined;
    this.tabPills = [];
    this.countdown = undefined;
    this.logTimer = undefined;
    this.shownCycle = -1;
    this.busy = false;
    this.tab = this.requestedTab ?? 'levels';
    if (this.tab === 'potions' && !this.potionsUnlocked()) this.tab = 'levels';

    // Backdrop: the market valley (dimmed) under the Market tab, a starfield
    // for the other two. Both are built once and toggled per tab.
    this.starfield = this.add.container(0, 0);
    // drawStarfield adds straight to the scene; wrap it in a container by
    // collecting what it creates.
    const before = this.children.list.length;
    drawStarfield(this, 1280, 720);
    const made = this.children.list.slice(before) as Phaser.GameObjects.GameObject[];
    this.starfield.add(made);
    this.backdrop = this.add.image(640, 360, 'bg-market').setDisplaySize(1280, 720).setDepth(-2);
    this.dim = this.add.rectangle(640, 360, 1280, 720, 0x000000, 0.45).setDepth(-1);

    this.add
      .text(cx, 48, 'THE HUB', { fontSize: '38px', color: HUB_RED_HEX, fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(5);

    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setDepth(5)
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => backBtn.setColor(GOLD_HEX));
    backBtn.on('pointerout', () => backBtn.setColor('#cfe0bd'));
    backBtn.on('pointerup', () => {
      if (this.busy) return;
      this.scene.start('MenuScene');
    });

    const coinIcon = this.add.image(960, 50, 'coin').setDisplaySize(34, 34).setDepth(5);
    spinCoin(this, coinIcon);
    this.balanceText = this.add
      .text(982, 50, coinLabel(this.progress.coins()), {
        fontSize: '22px',
        color: GOLD_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(5);

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    const music = getMusicService(this);
    music.playTrack(HUB_MENU_TRACK);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => music.stop());

    // Sweep achievements BEFORE the tabs: an award here can pay enough exp to
    // cross rank 10, and the Potions gate must be read after that, not before.
    // Welcome to the Hub: being here is the whole condition.
    this.award('welcomehub');
    sweepAndToastAchievements(saveStorage());
    // Quests: take a fresh period's baseline AFTER the sweep, so exp paid by
    // an award just now never counts as a rank-up toward a quest.
    try {
      quests().ensure(Date.now());
    } catch (err) {
      console.warn('[HubScene] quests ensure failed', err);
    }
    this.buildTabs(cx, 108);
    this.renderTab();
    if (this.openArchiveOnCreate && this.tab === 'events') this.openArchive();
    this.openArchiveOnCreate = false;
    installDebugReturn(this);
  }

  /** Award an achievement (idempotent) and toast it when newly earned. */
  private award(id: AchievementId): void {
    try {
      const a = this.rank.award(id);
      if (a) enqueueAchievementToast({ title: a.name, body: a.how, iconKey: a.icon });
    } catch (err) {
      console.warn('[HubScene] achievement award failed', err);
    }
  }

  private potionsUnlocked(): boolean {
    return potionShopUnlocked(this.rank.rank(), (f) => this.progress.hasFlag(f));
  }

  /** The Market sells from rank 10 (or the potionshop flag); below, it preaches. */
  private marketOpen(): boolean {
    return marketUnlocked(this.rank.rank(), (f) => this.progress.hasFlag(f));
  }

  // ── Tabs ─────────────────────────────────────────────────────────────────

  private buildTabs(centreX: number, y: number): void {
    const defs: { tab: HubTab; label: string }[] = [
      { tab: 'levels', label: 'LEVELS' },
      { tab: 'quests', label: 'QUESTS' },
      { tab: 'market', label: 'MARKET' },
      // Locked shows dimmed (restyleTabs) and explains itself on a tap — six
      // 170-wide pills leave no room for the old ' · LOCKED' suffix.
      { tab: 'potions', label: 'POTIONS' },
      { tab: 'events', label: 'EVENTS' },
      { tab: 'explore', label: 'EXPLORE' },
    ];
    // Six pills since the Level Creator (2026-11-01): 170 wide, 18 apart →
    // centres 170 / 358 / 546 / 734 / 922 / 1110 (tests/e2e/hub.spec.ts TAB).
    const pillW = 170;
    const gap = 18;
    const totalW = defs.length * pillW + (defs.length - 1) * gap;
    let x = centreX - totalW / 2 + pillW / 2;
    for (const d of defs) {
      const txt = this.add
        .text(x, y, d.label, {
          fontSize: '16px',
          color: '#9aa6ad',
          fontStyle: 'bold',
          backgroundColor: '#15131f',
          padding: { x: 16, y: 8 },
        })
        .setOrigin(0.5)
        .setDepth(5)
        .setLetterSpacing(2)
        .setName(`hub-tab-${d.tab}`)
        .setInteractive({ useHandCursor: true });
      txt.on('pointerup', () => this.onTab(d.tab));
      this.tabPills.push({ tab: d.tab, txt });
      x += pillW + gap;
    }
    this.restyleTabs();
  }

  private accentFor(tab: HubTab): string {
    return tab === 'market'
      ? HUB_BLUE_HEX
      : tab === 'quests'
        ? QUEST_ORANGE_HEX
        : tab === 'potions'
          ? '#7ed67e'
          : tab === 'events'
            ? GOLD_HEX
            : tab === 'explore'
              ? EXPLORE_TEAL_HEX
              : HUB_RED_HEX;
  }

  private restyleTabs(): void {
    for (const { tab, txt } of this.tabPills) {
      const unlocked = tab !== 'potions' || this.potionsUnlocked();
      const selected = tab === this.tab;
      txt.setColor(selected ? this.accentFor(tab) : unlocked ? '#cfd3dc' : '#6a7078');
      txt.setBackgroundColor(selected ? '#262234' : '#15131f');
    }
  }

  private onTab(tab: HubTab): void {
    if (this.busy || tab === this.tab) return;
    if (tab === 'potions' && !this.potionsUnlocked()) {
      const hint = this.add
        .text(640, 138, POTION_LOCK_HINT, {
          fontSize: '15px',
          color: '#7ed67e',
          fontStyle: 'italic',
        })
        .setOrigin(0.5)
        .setDepth(50)
        .setName('hub-potion-lock-hint');
      this.time.delayedCall(1800, () => hint.destroy());
      return;
    }
    this.tab = tab;
    this.restyleTabs();
    this.renderTab();
  }

  private renderTab(): void {
    this.body?.destroy();
    this.countdown = undefined;
    this.questCountdown = undefined;
    const body = this.add.container(0, 0).setDepth(1);
    this.body = body;
    const market = this.tab === 'market';
    this.backdrop?.setVisible(market);
    this.dim?.setVisible(market);
    this.starfield?.setVisible(!market);
    if (this.tab === 'levels') this.buildLevels(body);
    else if (this.tab === 'quests') this.buildQuests(body);
    else if (this.tab === 'market') this.buildMarket(body);
    else if (this.tab === 'events') this.buildEvents(body);
    else if (this.tab === 'explore') this.buildExplore(body);
    else this.buildPotions(body);
  }

  // ── LEVELS ───────────────────────────────────────────────────────────────

  private buildLevels(body: Phaser.GameObjects.Container): void {
    HUB_CHAPTERS.forEach((chapter, i) => {
      const rowY = AREA_TOP + SLOT_H * i + SLOT_H / 2;
      this.makeChapterCard(640, rowY, chapter, body);
    });
  }

  /** One chapter card in the episode-row style: "01" badge, name, moon wash. */
  private makeChapterCard(
    cx: number,
    rowY: number,
    chapter: HubChapterDef,
    body: Phaser.GameObjects.Container,
  ): void {
    const left = cx - ROW_W / 2;
    const top = rowY - ROW_H / 2;
    const right = cx + ROW_W / 2;
    // The chapter's OWN levels — the Hub's cursor is shared, so it is offset
    // by the chapters before this one (raw, it would read "3 / 3" on The Fungi
    // Forest the moment The Moon was cleared). A granted cursor (`/give
    // hublevel`) writes no stars, so the larger of the two counts.
    const before = HUB_CHAPTERS.slice(0, HUB_CHAPTERS.indexOf(chapter)).reduce(
      (n, c) => n + c.levels.length,
      0,
    );
    const len = chapter.levels.length;
    const viaCursor = Math.max(0, Math.min(this.progress.hubCompleted() - before, len));
    const starred = chapter.levels.filter((l) => this.progress.stars(l.id) >= 1).length;
    const done = Math.max(starred, viaCursor);
    const cleared = chapter.levels.every((l) => this.progress.stars(l.id) >= 1);
    const locked = !isHubLevelUnlocked(chapter.levels[0].id, this.progress.hubCompleted());

    const panel = this.add.graphics();
    body.add(panel);
    const drawPanel = (hover: boolean) => {
      panel.clear();
      panel.fillStyle(0x15131f, hover ? 0.95 : 0.8);
      panel.fillRoundedRect(left, top, ROW_W, ROW_H, 12);
      panel.lineStyle(hover ? 2 : 1, chapter.color, hover ? 1 : 0.5);
      panel.strokeRoundedRect(left, top, ROW_W, ROW_H, 12);
      panel.fillStyle(chapter.color, 1);
      panel.fillRoundedRect(left, top, 6, ROW_H, { tl: 12, bl: 12, tr: 0, br: 0 });
    };
    drawPanel(false);

    // The moon, washed into the LEFT of the card (spec: "on the very left").
    if (this.textures.exists(chapter.art)) {
      const wash = this.add
        .image(left + 150, rowY, chapter.art)
        .setDepth(0.5)
        .setAlpha(0.55);
      const s = Math.max(300 / wash.width, ROW_H / wash.height);
      wash.setScale(s);
      const maskG = this.make.graphics();
      maskG.fillStyle(0xffffff);
      maskG.fillRoundedRect(left, top, 300, ROW_H, 12);
      wash.setMask(maskG.createGeometryMask());
      wash.once('destroy', () => maskG.destroy());
      body.add(wash);
    }

    body.add(
      this.add
        .text(left + 30, rowY, chapter.number.toString().padStart(2, '0'), {
          fontSize: '44px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(1)
        .setShadow(0, 2, '#000000', 6),
    );
    body.add(
      this.add
        .text(left + 330, rowY - 22, 'CHAPTER', {
          fontSize: '13px',
          color: chapter.colorHex,
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(1),
    );
    body.add(
      this.add
        .text(left + 330, rowY + 8, chapter.name, {
          fontSize: '27px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(1)
        .setName(`hub-chapter-${chapter.number}`),
    );
    const progressLine = cleared
      ? 'CLEARED'
      : locked
        ? 'LOCKED'
        : `${done} / ${chapter.levels.length} LEVELS`;
    body.add(
      this.add
        .text(right - 24, rowY, progressLine, {
          fontSize: '18px',
          color: cleared ? '#5bd35b' : locked ? '#9aa6ad' : GOLD_HEX,
          fontStyle: 'bold',
        })
        .setOrigin(1, 0.5)
        .setDepth(1)
        .setName(`hub-chapter-progress-${chapter.number}`),
    );

    const hit = this.add.zone(cx, rowY, ROW_W, ROW_H).setOrigin(0.5);
    body.add(hit);
    hit.setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => drawPanel(true));
    hit.on('pointerout', () => drawPanel(false));
    hit.on('pointerup', () => {
      if (this.busy) return;
      this.scene.start('HubChapterScene', { chapter: chapter.number });
    });
  }

  // ── MARKET ───────────────────────────────────────────────────────────────

  /**
   * The Market (spec Revision 6, a third row in Revision 7): the valley
   * traders sell Lightning (400), Spear (300) and Bow Hambi (500) for the
   * same persistent coins as the Shop, into the same roster. One row each; a
   * bought one reads OWNED. Below rank 10 (Revision 8) the tab opens all the
   * same, but onto the Wise Hambi's counsel instead of the rows.
   */
  private buildMarket(body: Phaser.GameObjects.Container): void {
    if (!this.marketOpen()) {
      this.buildMarketLocked(body);
      return;
    }
    body.add(
      this.add
        .text(640, 152, 'MARKET', { fontSize: '30px', color: HUB_BLUE_HEX, fontStyle: 'bold' })
        .setOrigin(0.5)
        .setShadow(0, 2, '#000000', 4),
    );
    body.add(
      this.add
        .text(640, 186, 'Hambis from the valley traders. A bought Hambi joins your roster.', {
          fontSize: '14px',
          color: '#b8c4c6',
          fontStyle: 'italic',
        })
        .setOrigin(0.5)
        .setShadow(0, 1, '#000000', 3),
    );
    MARKET_TOWERS.forEach((type, i) => this.makeMarketRow(640, MARKET_ROW_YS[i], type, body));
  }

  /**
   * The Market below rank 10: one line of counsel, and the evolved Wise Hambi
   * under it. No rows, no prices — the page IS the lock message, so the tab
   * pill stays plain "MARKET" (unlike Potions, which refuses with a hint).
   */
  private buildMarketLocked(body: Phaser.GameObjects.Container): void {
    body.add(
      this.add
        .text(640, 152, 'MARKET', { fontSize: '30px', color: HUB_BLUE_HEX, fontStyle: 'bold' })
        .setOrigin(0.5)
        .setShadow(0, 2, '#000000', 4),
    );
    body.add(
      this.add
        .text(640, 262, MARKET_LOCK_TEXT, {
          fontSize: '26px',
          color: '#f0e6c8',
          fontStyle: 'italic',
          align: 'center',
          // Wide enough for the whole line at 26px, so "great." is never orphaned.
          wordWrap: { width: 1100 },
        })
        .setOrigin(0.5)
        .setShadow(0, 2, '#000000', 4)
        .setName('hub-market-locked-text'),
    );
    body.add(
      this.add
        .image(640, 470, MARKET_LOCK_ART)
        .setDisplaySize(240, 240)
        .setName('hub-market-locked-wise'),
    );
  }

  private makeMarketRow(
    cx: number,
    y: number,
    type: ShopTowerType,
    body: Phaser.GameObjects.Container,
  ): void {
    const left = cx - ROW_W / 2;
    const right = cx + ROW_W / 2;
    const price = SHOP_PRICES[type];
    const owned = this.progress.hasTower(type);
    const affordable = this.progress.coins() >= price;

    const panel = this.add.graphics();
    panel.fillStyle(0x0f1a2e, 0.88);
    panel.fillRoundedRect(left, y - MARKET_ROW_H / 2, ROW_W, MARKET_ROW_H, 12);
    panel.lineStyle(owned ? 2 : 1, HUB_BLUE, owned ? 0.9 : 0.5);
    panel.strokeRoundedRect(left, y - MARKET_ROW_H / 2, ROW_W, MARKET_ROW_H, 12);
    body.add(panel);

    const art = `tower-${type}-1`;
    if (this.textures.exists(art)) {
      body.add(this.add.image(left + 74, y, art).setDisplaySize(100, 100));
    }
    body.add(
      this.add
        .text(left + 140, y - 40, TOWER_NAMES[type], {
          fontSize: '22px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setName(`hub-market-${type}-name`),
    );
    body.add(
      this.add
        .text(left + 140, y + 8, TOWER_TUTORIALS[type], {
          fontSize: '13px',
          color: '#9fd8ff',
          wordWrap: { width: 440 },
          lineSpacing: 3,
        })
        .setOrigin(0, 0.5),
    );

    if (owned) {
      body.add(
        this.add
          .text(right - 105, y, 'OWNED', {
            fontSize: '24px',
            color: HUB_BLUE_HEX,
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setName(`hub-market-${type}-owned`),
      );
      return;
    }

    body.add(this.add.image(right - 250, y, 'coin').setDisplaySize(30, 30));
    body.add(
      this.add
        .text(right - 228, y, `${price}`, {
          fontSize: '22px',
          color: affordable ? GOLD_HEX : '#7f8c8d',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5),
    );
    const btn = this.add
      .rectangle(right - 105, y, 150, 52, affordable ? GOLD : 0x2a2a3a)
      .setStrokeStyle(2, affordable ? 0xfff3b0 : 0x555a66)
      .setInteractive({ useHandCursor: affordable })
      .setDepth(2)
      .setName(`hub-market-${type}-buy`);
    body.add(btn);
    body.add(
      this.add
        .text(right - 105, y, 'BUY', {
          fontSize: '20px',
          color: affordable ? '#1a140a' : '#7f8c8d',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(3),
    );
    if (affordable) {
      btn.on('pointerover', () => btn.setFillStyle(0xffd83a));
      btn.on('pointerout', () => btn.setFillStyle(GOLD));
    }
    btn.on('pointerup', () => {
      if (this.busy) return;
      if (!affordable) {
        flashHint(this, 'Not enough coins — win episodes to earn more.');
        return;
      }
      this.confirmTowerPurchase(type);
    });
  }

  /** BUY a Market Hambi → confirm → purchaseTower → Sales! → redraw as OWNED. */
  private confirmTowerPurchase(type: ShopTowerType): void {
    const price = SHOP_PRICES[type];
    const W = 1280;
    const H = 720;
    const D = 100;
    this.busy = true;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      return o;
    };
    add(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.7)
        .setInteractive()
        .setDepth(D),
    );
    add(
      this.add
        .rectangle(W / 2, H / 2, 560, 260, 0x0f1a2e, 1)
        .setStrokeStyle(2, HUB_BLUE)
        .setDepth(D + 1),
    );
    add(
      this.add
        .text(W / 2, H / 2 - 76, `Buy ${TOWER_NAMES[type]}?`, {
          fontSize: '28px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    add(
      this.add
        .text(
          W / 2,
          H / 2 - 22,
          `${price} Hambi coins.\nJoins your roster for good — equip it before a level.`,
          {
            fontSize: '16px',
            color: '#9aa6ad',
            align: 'center',
            lineSpacing: 4,
          },
        )
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    const teardown = () => {
      objs.forEach((o) => o.destroy());
      this.busy = false;
    };
    const confirmBg = add(
      this.add
        .rectangle(W / 2 - 120, H / 2 + 70, 200, 52, 0x1a6b1a)
        .setStrokeStyle(2, 0x4caf50)
        .setInteractive({ useHandCursor: true })
        .setDepth(D + 2)
        .setName('hub-market-confirm'),
    );
    add(
      this.add
        .text(W / 2 - 120, H / 2 + 70, 'BUY', {
          fontSize: '20px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    confirmBg.on('pointerup', () => {
      if (!this.progress.purchaseTower(type, price)) {
        teardown();
        flashHint(this, 'Not enough coins — win episodes to earn more.');
        return;
      }
      recordStat('obtains'); // Quests: a new tower
      this.award('sales');
      sweepAndToastAchievements(saveStorage());
      this.balanceText.setText(coinLabel(this.progress.coins()));
      teardown();
      this.renderTab();
    });
    const cancelBg = add(
      this.add
        .rectangle(W / 2 + 120, H / 2 + 70, 200, 52, 0x2a2a3a)
        .setStrokeStyle(2, 0x6666aa)
        .setInteractive({ useHandCursor: true })
        .setDepth(D + 2)
        .setName('hub-market-cancel'),
    );
    add(
      this.add
        .text(W / 2 + 120, H / 2 + 70, 'CANCEL', {
          fontSize: '20px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    cancelBg.on('pointerup', teardown);
  }

  // ── EVENTS: the Origin Event (spec 2026-10-06) ───────────────────────────

  /**
   * The EVENTS tab: the Origin Event. "ORIGIN EVENT" across the top, the
   * Update Log and Archive stars in the top-right corner with the Dark Hambi
   * Coin balance beside them (where the Capycoins sat), and two tall cards —
   * ABOVE THE CLOUDS on the left, THE DARK SHADOWS on the right — each opening
   * its own level menu (`OriginChapterScene`). The Capybara Event that used to
   * live here closed on 2026-10-06; its minigame is in the Archive.
   */
  // ── EXPLORE (the Level Creator, spec 2026-11-01) ──────────────────────────

  /**
   * Two big cards filling the tab: CREATE (the player's own levels) and
   * EXPLORE (every approved published level, spec 2026-11-02).
   */
  private buildExplore(body: Phaser.GameObjects.Container): void {
    const cards: { x: number; word: string; art: string; name: string; color: number }[] = [
      {
        x: 355,
        word: 'CREATE',
        art: 'hub-explore-create',
        name: 'hub-explore-create',
        color: EXPLORE_TEAL,
      },
      {
        x: 925,
        word: 'EXPLORE',
        art: 'hub-explore-explore',
        name: 'hub-explore-explore',
        color: 0x7b6cff,
      },
    ];
    const W = 530;
    const H = 500;
    const Y = 395;
    for (const c of cards) {
      const frame = this.add
        .rectangle(c.x, Y, W, H, 0x15131f, 1)
        .setStrokeStyle(4, c.color)
        .setInteractive({ useHandCursor: true })
        .setName(c.name);
      body.add(frame);
      if (this.textures.exists(c.art)) {
        const img = this.add.image(c.x, Y - 40, c.art);
        img.setScale(Math.min((W - 8) / img.width, (H - 96) / img.height));
        body.add(img);
      }
      body.add(this.add.rectangle(c.x, Y + H / 2 - 44, W - 8, 80, c.color, 0.92));
      body.add(
        this.add
          .text(c.x, Y + H / 2 - 44, c.word, {
            fontSize: '40px',
            color: '#ffffff',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setLetterSpacing(4)
          .setStroke('#0b0a12', 6),
      );
      frame.on('pointerover', () => frame.setStrokeStyle(6, 0xffffff));
      frame.on('pointerout', () => frame.setStrokeStyle(4, c.color));
      frame.on('pointerup', () => {
        if (this.busy) return;
        this.scene.start(c.word === 'CREATE' ? 'CreatorMenuScene' : 'ExploreScene');
      });
    }
  }

  private buildEvents(body: Phaser.GameObjects.Container): void {
    body.add(this.add.rectangle(640, 174, 1280, 84, 0x0a0806, 0.45));
    body.add(
      this.add
        .text(640, 172, 'ORIGIN EVENT', {
          fontSize: '40px',
          color: '#f0e6ff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setStroke('#2a1240', 6)
        .setLetterSpacing(3)
        .setName('hub-events-title'),
    );

    // The Update Log star: the tab's top-right corner, 44px.
    const square = this.add
      .rectangle(1196, 172, 44, 44, GOLD)
      .setStrokeStyle(3, 0xfff3b0)
      .setInteractive({ useHandCursor: true })
      .setName('hub-events-star');
    body.add(square);
    body.add(this.add.text(1196, 171, '★', { fontSize: '30px', color: '#1a140a' }).setOrigin(0.5));
    square.on('pointerover', () => square.setFillStyle(0xffd83a));
    square.on('pointerout', () => square.setFillStyle(GOLD));
    square.on('pointerup', () => {
      if (this.busy) return;
      this.openUpdateLog();
    });
    body.add(
      this.add
        .text(1196, 206, 'UPDATE LOG', { fontSize: '10px', color: '#f4e9c8' })
        .setOrigin(0.5)
        .setLetterSpacing(1)
        .setShadow(0, 1, '#000000', 2),
    );

    // The Event Archive (spec 2026-10-04): the same 44px star square directly
    // under the Update Log's, in light blue. Every past event's minigame.
    const archive = this.add
      .rectangle(1196, 250, 44, 44, ARCHIVE_BLUE)
      .setStrokeStyle(3, 0xe0f5ff)
      .setInteractive({ useHandCursor: true })
      .setName('hub-events-archive');
    body.add(archive);
    body.add(this.add.text(1196, 249, '★', { fontSize: '30px', color: '#0d2233' }).setOrigin(0.5));
    archive.on('pointerover', () => archive.setFillStyle(ARCHIVE_BLUE_HOVER));
    archive.on('pointerout', () => archive.setFillStyle(ARCHIVE_BLUE));
    archive.on('pointerup', () => {
      if (this.busy) return;
      this.openArchive();
    });
    body.add(
      this.add
        .text(1196, 284, 'ARCHIVE', { fontSize: '10px', color: '#e0f5ff' })
        .setOrigin(0.5)
        .setLetterSpacing(1)
        .setShadow(0, 1, '#000000', 2),
    );

    // The Dark Hambi Coin balance, just left of the star — the Capycoins' spot.
    // A pale slate pill: the coin is black, and vanishes against the usual dark one.
    body.add(this.add.rectangle(1108, 172, 128, 36, 0x4a4f5c, 0.9).setStrokeStyle(2, 0x9aa0ad));
    const darkIcon = this.add.image(1064, 172, 'darkcoin').setDisplaySize(28, 28);
    body.add(darkIcon);
    spinCoin(this, darkIcon, DARKCOIN_SPIN_FRAMES);
    body.add(
      this.add
        .text(1084, 172, `${this.progress.darkCoins()}`, {
          fontSize: '20px',
          color: '#d7dbe2',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setName('hub-events-darkcoins'),
    );
    // And the Light Hambi Coins (Above the Clouds, spec 2026-10-08) just left
    // of it, on a slate pill a shade deeper so the white coin reads. (Under it,
    // it covered the Update Log star's caption.)
    body.add(this.add.rectangle(972, 172, 128, 36, 0x3a4458, 0.9).setStrokeStyle(2, 0xb8c8dc));
    const lightIcon = this.add.image(928, 172, 'lightcoin').setDisplaySize(28, 28);
    body.add(lightIcon);
    spinCoin(this, lightIcon, LIGHTCOIN_SPIN_FRAMES);
    body.add(
      this.add
        .text(948, 172, `${this.progress.lightCoins()}`, {
          fontSize: '20px',
          color: '#eaf2fa',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setName('hub-events-lightcoins'),
    );

    // Decoration (2026-10-06): a Ghost haunting the bottom-left corner and a
    // big Dark Ghost the bottom-right — the enemies' own sprites, as
    // see-through as they are on the track. Not interactive: Phaser listens on
    // the window, and a decoration must never eat a tap meant for the board.
    for (const g of EVENT_GHOSTS) {
      const ghost = this.add
        .image(g.x, g.y, g.key)
        .setDisplaySize(g.size, g.size)
        .setAlpha(ENEMY_ALPHA[g.type] ?? 1)
        .setName(g.name);
      if (g.flip) ghost.setFlipX(true);
      body.add(ghost);
      this.tweens.add({
        targets: ghost,
        y: g.y - 10,
        duration: g.bobMs,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    ORIGIN_CHAPTERS.forEach((chapter, i) => {
      this.makeOriginCard(body, ORIGIN_CARD_XS[i], chapter);
    });
  }

  /**
   * One Origin chapter: a tall rectangle (taller than it is wide) holding the
   * chapter's cover art, its name underneath. A tap opens its level menu.
   */
  private makeOriginCard(
    body: Phaser.GameObjects.Container,
    cx: number,
    chapter: OriginChapterDef,
  ): void {
    const { w, h, y } = ORIGIN_CARD;
    const accent = chapter.id === 'shadows' ? 0xe0562a : 0x7fc8ff;
    const accentHex = chapter.id === 'shadows' ? '#ff8a5c' : '#a8dcff';
    body.add(this.add.image(cx, y, chapter.art).setDisplaySize(w, h));
    const frame = this.add
      .rectangle(cx, y, w, h)
      .setStrokeStyle(4, accent, 0.85)
      .setInteractive({ useHandCursor: true })
      .setName(`hub-origin-${chapter.id}`);
    body.add(frame);
    body.add(
      this.add
        .text(cx, y + h / 2 + 30, chapter.name, {
          fontSize: '24px',
          color: accentHex,
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setStroke('#0a0806', 5)
        .setName(`hub-origin-label-${chapter.id}`),
    );
    frame.on('pointerover', () => frame.setStrokeStyle(6, 0xffffff, 1));
    frame.on('pointerout', () => frame.setStrokeStyle(4, accent, 0.85));
    frame.on('pointerup', () => {
      if (this.busy) return;
      this.scene.start('OriginChapterScene', { chapter: chapter.id });
    });
    this.makeOriginShopButton(body, cx, chapter);
  }

  /**
   * The chapter's shop (spec 2026-10-12), under its card and name: BLACK with
   * white text under The Dark Shadows, WHITE with black text under Above the
   * Clouds, each with a gem beside "SHOP".
   */
  private makeOriginShopButton(
    body: Phaser.GameObjects.Container,
    cx: number,
    chapter: OriginChapterDef,
  ): void {
    const dark = chapter.id === 'shadows';
    const w = 180;
    const h = 44;
    const y = ORIGIN_CARD.y + ORIGIN_CARD.h / 2 + 76;
    const bg = this.add.graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      bg.fillStyle(dark ? (hover ? 0x2a2a30 : 0x0b0b0e) : hover ? 0xe6e6ee : 0xffffff, 1);
      bg.fillRoundedRect(cx - w / 2, y - h / 2, w, h, 10);
      bg.lineStyle(2, dark ? 0xffffff : 0x0b0b0e, hover ? 1 : 0.8);
      bg.strokeRoundedRect(cx - w / 2, y - h / 2, w, h, 10);
    };
    draw(false);
    body.add(bg);
    body.add(this.add.image(cx - 40, y, 'diamond').setDisplaySize(26, 26));
    body.add(
      this.add
        .text(cx + 16, y, 'SHOP', {
          fontSize: '22px',
          fontStyle: 'bold',
          color: dark ? '#ffffff' : '#0b0b0e',
        })
        .setOrigin(0.5)
        .setName(`hub-origin-shop-label-${chapter.id}`),
    );
    const hit = this.add
      .zone(cx, y, w, h)
      .setName(`hub-origin-shop-${chapter.id}`)
      .setInteractive({ useHandCursor: true });
    body.add(hit);
    hit.on('pointerover', () => draw(true));
    hit.on('pointerout', () => draw(false));
    hit.on('pointerup', () => {
      if (this.busy) return;
      this.scene.start('OriginShopScene', { shop: dark ? 'dark' : 'light' });
    });
  }

  /**
   * The Event Archive (spec 2026-10-04): a card per past event, each with
   * PLAY into its minigame's archive mode. Opened by the light-blue star, and
   * again on arrival when a run's BACK TO ARCHIVE brings the player home.
   */
  private openArchive(): void {
    const W = 1280;
    const H = 720;
    const D = LOG_D;
    this.busy = true;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      return o;
    };
    const teardown = () => {
      objs.forEach((o) => o.destroy());
      this.busy = false;
    };

    add(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.75)
        .setInteractive()
        .setDepth(D),
    );
    const panelW = 820;
    // Sized to its cards (one today), so a short archive is not a big empty box.
    const panelH = 92 + ARCHIVED_EVENTS.length * (132 + 14) + 10;
    const panelX = (W - panelW) / 2;
    const panelY = Math.max(140, (H - panelH) / 2);
    add(
      this.add
        .rectangle(W / 2, panelY + panelH / 2, panelW, panelH, 0x0f1b26, 1)
        .setStrokeStyle(2, ARCHIVE_BLUE)
        .setDepth(D + 1)
        .setName('hub-archive-panel'),
    );
    add(
      this.add
        .text(panelX + 24, panelY + 28, '★ EVENT ARCHIVE', {
          fontSize: '26px',
          color: ARCHIVE_BLUE_HEX,
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setLetterSpacing(2)
        .setDepth(D + 2),
    );
    add(
      this.add
        .text(
          panelX + 24,
          panelY + 58,
          'Every event that has come and gone — play its minigame again.',
          {
            fontSize: '15px',
            color: '#9fb6c6',
            fontStyle: 'italic',
          },
        )
        .setOrigin(0, 0.5)
        .setDepth(D + 2),
    );
    const close = add(
      this.add
        .text(panelX + panelW - 30, panelY + 28, '✕', {
          fontSize: '26px',
          color: '#cfd3dc',
          padding: { x: 10, y: 4 },
        })
        .setOrigin(0.5)
        .setDepth(D + 2)
        .setInteractive({ useHandCursor: true })
        .setName('hub-archive-close'),
    );
    close.on('pointerover', () => close.setColor(ARCHIVE_BLUE_HEX));
    close.on('pointerout', () => close.setColor('#cfd3dc'));
    close.on('pointerup', teardown);

    // One card per archived event, oldest first.
    const cardW = panelW - 48;
    const cardH = 132;
    ARCHIVED_EVENTS.forEach((ev, i) => {
      const cy = panelY + 92 + cardH / 2 + i * (cardH + 14);
      this.buildArchiveCard(ev, W / 2, cy, cardW, cardH, D + 2, add);
    });
  }

  /** One archived event: thumbnail, name, what you play, the prize, PLAY. */
  private buildArchiveCard(
    ev: ArchivedEvent,
    cx: number,
    cy: number,
    w: number,
    h: number,
    depth: number,
    add: <T extends Phaser.GameObjects.GameObject>(o: T) => T,
  ): void {
    const left = cx - w / 2;
    add(
      this.add
        .rectangle(cx, cy, w, h, 0x162a3a, 1)
        .setStrokeStyle(1, 0x2f5a78)
        .setDepth(depth)
        .setName(`hub-archive-card-${ev.id}`),
    );
    if (this.textures.exists(ev.art)) {
      add(
        this.add
          .image(left + 70, cy, ev.art)
          .setDisplaySize(100, 100)
          .setDepth(depth + 1),
      );
    }
    add(
      this.add
        .text(left + 140, cy - 38, ev.title, {
          fontSize: '24px',
          color: '#f4e9c8',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(depth + 1),
    );
    add(
      this.add
        .text(left + 140, cy - 6, ev.subtitle, {
          fontSize: '15px',
          color: '#cfd3dc',
          // Clear of PLAY, which starts 160px in from the card's right edge.
          wordWrap: { width: w - 140 - 180 },
        })
        .setOrigin(0, 0.5)
        .setDepth(depth + 1),
    );
    if (this.textures.exists(ev.prizeArt)) {
      add(
        this.add
          .image(left + 154, cy + 34, ev.prizeArt)
          .setDisplaySize(30, 30)
          .setDepth(depth + 1),
      );
    }
    add(
      this.add
        .text(left + 176, cy + 34, ev.prize, {
          fontSize: '15px',
          color: '#e0b98a',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setDepth(depth + 1),
    );
    const play = add(
      this.add
        .rectangle(left + w - 90, cy, 140, 52, 0x6b3e1e)
        .setStrokeStyle(3, 0x3b2410)
        .setDepth(depth + 1)
        .setInteractive({ useHandCursor: true })
        .setName(`hub-archive-play-${ev.id}`),
    );
    add(
      this.add
        .text(left + w - 90, cy, 'PLAY', {
          fontSize: '24px',
          color: '#f4e9c8',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setLetterSpacing(4)
        .setDepth(depth + 2),
    );
    play.on('pointerover', () => play.setFillStyle(0x8a5430));
    play.on('pointerout', () => play.setFillStyle(0x6b3e1e));
    play.on('pointerup', () => {
      // The archive run: no Capycoins, a Capystone, and every way out leads
      // back to this list (spec 2026-10-04 §2).
      this.scene.start('CapybaraScene', {
        mode: 'archive',
        returnTo: 'HubScene',
        returnData: { tab: 'events', archive: true },
      });
    });
  }

  /**
   * The Update Log: a list of updates on the left (rows separated by lines)
   * and a detail panel on the right — status at the top (COMING SOON until
   * the release instant, RELEASED after), a thumbnail composed from the
   * game's own art, the release date, a countdown that ticks live and, when
   * the entry carries one, a line saying what the update brings. Opens on
   * the OLDEST update.
   */
  private openUpdateLog(): void {
    const W = 1280;
    const H = 720;
    const D = LOG_D;
    this.busy = true;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      return o;
    };
    let detail: Phaser.GameObjects.GameObject[] = [];
    const teardown = () => {
      this.logTimer = undefined;
      for (const o of detail) {
        this.tweens.killTweensOf(o);
        o.destroy();
      }
      detail = [];
      objs.forEach((o) => o.destroy());
      this.busy = false;
    };

    add(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.75)
        .setInteractive()
        .setDepth(D),
    );
    const panelX = 90;
    const panelY = 86;
    const panelW = 1100;
    const panelH = 566;
    add(
      this.add
        .rectangle(W / 2, panelY + panelH / 2, panelW, panelH, 0x15131f, 1)
        .setStrokeStyle(2, GOLD)
        .setDepth(D + 1)
        .setName('hub-log-panel'),
    );
    add(
      this.add
        .text(panelX + 24, panelY + 26, 'UPDATE LOG', {
          fontSize: '26px',
          color: GOLD_HEX,
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setLetterSpacing(2)
        .setDepth(D + 2),
    );
    const close = add(
      this.add
        .text(panelX + panelW - 30, panelY + 26, '✕', {
          fontSize: '26px',
          color: '#cfd3dc',
          padding: { x: 10, y: 4 },
        })
        .setOrigin(0.5)
        .setDepth(D + 2)
        .setInteractive({ useHandCursor: true })
        .setName('hub-log-close'),
    );
    close.on('pointerover', () => close.setColor(GOLD_HEX));
    close.on('pointerout', () => close.setColor('#cfd3dc'));
    close.on('pointerup', teardown);

    // The list: one row per update, oldest first, a line under each.
    const listX = panelX + 24;
    const listW = 300;
    const listTop = panelY + 62;
    const rowH = 54;
    const rows: { entry: UpdateEntry; txt: Phaser.GameObjects.Text }[] = [];
    const lines = add(this.add.graphics().setDepth(D + 2));
    lines.lineStyle(1, 0x3a3f4a, 1);
    UPDATES.forEach((entry, i) => {
      const y = listTop + rowH * i;
      const txt = add(
        this.add
          .text(listX + 12, y + rowH / 2, entry.title, {
            fontSize: '17px',
            color: '#cfd3dc',
            fontStyle: 'bold',
          })
          .setOrigin(0, 0.5)
          .setDepth(D + 3)
          .setName(`hub-log-entry-${entry.id}`),
      );
      // A long title ("Update 1.1: The Hub Extension") overruns the column and
      // its divider at 17px; drop it a notch at a time rather than let it hang
      // over ("Update 1.4: The Golden Takeout Bag" needs more than one).
      for (let size = 15; txt.width > listW - 24 && size >= 12; size--) txt.setFontSize(size);
      const hit = add(
        this.add
          .rectangle(listX + listW / 2, y + rowH / 2, listW, rowH, 0xffffff, 0)
          .setDepth(D + 2)
          .setInteractive({ useHandCursor: true }),
      );
      hit.on('pointerup', () => select(entry));
      lines.lineBetween(listX, y + rowH, listX + listW, y + rowH);
      rows.push({ entry, txt });
    });

    // The detail panel: status / thumbnail / date / countdown, top to bottom.
    const dx = panelX + listW + 60;
    const dw = panelW - listW - 84;
    const dTop = listTop;
    const dH = panelH - 62 - 24;
    const cx = dx + dw / 2;
    add(
      this.add
        .rectangle(cx, dTop + dH / 2, dw, dH, 0x0f0e16, 1)
        .setStrokeStyle(1, 0x3a3f4a)
        .setDepth(D + 2)
        .setName('hub-log-detail'),
    );
    const select = (entry: UpdateEntry) => {
      for (const r of rows) r.txt.setColor(r.entry === entry ? GOLD_HEX : '#cfd3dc');
      for (const o of detail) {
        this.tweens.killTweensOf(o);
        o.destroy();
      }
      detail = [];
      const now = Date.now();
      const status = this.add
        .text(cx, dTop + 34, updateStatus(entry, now), {
          fontSize: '28px',
          color: updateStatus(entry, now) === 'RELEASED' ? '#7ed67e' : GOLD_HEX,
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setLetterSpacing(3)
        .setDepth(D + 3)
        .setName('hub-log-status');
      detail.push(status);
      // The thumbnail: the Moon chapter's own wash with the game's potions and
      // Bow Hambi (tier 2) floating around it, all clipped to a rounded frame.
      const tw = 400;
      const th = 210;
      const ty = dTop + 70 + th / 2;
      const frame = this.add.graphics().setDepth(D + 3);
      frame.fillStyle(0x0b1a33, 1);
      frame.fillRoundedRect(cx - tw / 2, ty - th / 2, tw, th, 10);
      frame.lineStyle(2, 0x3a3f4a, 1);
      frame.strokeRoundedRect(cx - tw / 2, ty - th / 2, tw, th, 10);
      detail.push(frame);
      const maskG = this.make.graphics();
      maskG.fillStyle(0xffffff);
      maskG.fillRoundedRect(cx - tw / 2, ty - th / 2, tw, th, 10);
      const mask = maskG.createGeometryMask();
      detail.push(maskG);
      if (this.textures.exists(entry.thumbnail.backdrop)) {
        const bg = this.add.image(cx, ty, entry.thumbnail.backdrop).setDepth(D + 3);
        bg.setScale(Math.max(tw / bg.width, th / bg.height));
        bg.setMask(mask);
        detail.push(bg);
      }
      // 'arc': two arcs of floaters, left and right of the moon. 'hero': the
      // FIRST key stands tall in the middle with the rest either side of it.
      // Both bob on their own beats.
      // 'beside': the FIRST key stands tall on the LEFT, on the backdrop's
      // floor, leaving the right of the frame to the backdrop's own subject.
      // 'ring': the FIRST key large in the middle, the rest spaced evenly on
      // an ellipse around it, clockwise from upper right. Starting at −60°
      // rather than straight up keeps a floater off the middle key's head and
      // feet, which the 210-tall frame leaves no room for.
      const hero = entry.thumbnail.layout === 'hero';
      const beside = entry.thumbnail.layout === 'beside';
      const ring = entry.thumbnail.layout === 'ring';
      // 'rows': two rows of three on the backdrop's seam, close enough that
      // both sides of the backdrop stay in view.
      const rows2 = entry.thumbnail.layout === 'rows';
      const around = entry.thumbnail.floaters.length - 1;
      const spots = rows2
        ? [-48, 50].flatMap((y) => [-78, 0, 78].map((x) => ({ x, y })))
        : ring
          ? [
              { x: 0, y: 0 },
              ...Array.from({ length: around }, (_, k) => {
                const a = -Math.PI / 3 + (2 * Math.PI * k) / Math.max(1, around);
                return { x: Math.round(Math.cos(a) * 160), y: Math.round(Math.sin(a) * 66) };
              }),
            ]
          : beside
            ? [{ x: -115, y: 12 }]
            : hero
              ? [
                  { x: 0, y: 0 },
                  { x: -142, y: 16 },
                  { x: 142, y: 16 },
                ]
              : [
                  { x: -150, y: -50 },
                  { x: -130, y: 55 },
                  { x: 0, y: -10 },
                  { x: 135, y: -55 },
                  { x: 150, y: 50 },
                ];
      entry.thumbnail.floaters.forEach((key, i) => {
        if (!this.textures.exists(key)) return;
        const spot = spots[i % spots.length];
        // The hero is drawn 170 tall and keeps its aspect — decor-rocket is
        // 256 x 384, and a square setDisplaySize would squash it.
        const src = this.textures.get(key).getSourceImage();
        const tall = (hero || beside || ring) && i === 0;
        const heroH = beside ? 150 : ring ? 120 : 170;
        const side = hero ? 64 : ring ? 60 : rows2 ? 72 : key.startsWith('tower-') ? 84 : 56;
        const w = tall ? Math.round((heroH * src.width) / src.height) : side;
        const h = tall ? heroH : side;
        const img = this.add
          .image(cx + spot.x, ty + spot.y, key)
          .setDisplaySize(w, h)
          .setDepth(D + 4)
          .setName(`hub-log-floater-${key}`);
        img.setMask(mask);
        detail.push(img);
        // The hero's caption (Update 1.6's MUSHROOMS), printed across the top
        // third of the art — a mushroom's cap — and bobbing with it.
        const caption =
          i === 0 && entry.thumbnail.caption
            ? this.add
                .text(img.x, img.y - h * 0.2, entry.thumbnail.caption, {
                  fontSize: '26px',
                  color: '#ffffff',
                  fontStyle: 'bold',
                  stroke: '#3a1a08',
                  strokeThickness: 6,
                })
                .setOrigin(0.5)
                .setDepth(D + 5)
                .setName('hub-log-caption')
            : undefined;
        if (caption) {
          caption.setMask(mask);
          detail.push(caption);
        }
        this.tweens.add({
          targets: caption ? [img, caption] : img,
          y: `-=8`,
          // A captioned hero only bobs: tilting would slide the word off the cap.
          angle: caption ? 0 : i % 2 === 0 ? 6 : -6,
          duration: 1100 + i * 170,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      });
      detail.push(
        this.add
          .text(cx, ty + th / 2 + 34, entry.dateLabel, {
            fontSize: '22px',
            color: '#ffffff',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(D + 3)
          .setName('hub-log-date'),
      );
      const countdown = this.add
        .text(cx, ty + th / 2 + 72, updateCountdown(entry, now), {
          fontSize: '26px',
          fontFamily: 'monospace',
          color: GOLD_HEX,
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3)
        .setName('hub-log-countdown');
      detail.push(countdown);
      // What the update brings, under the timer.
      if (entry.description) {
        detail.push(
          this.add
            .text(cx, ty + th / 2 + 110, entry.description, {
              fontSize: '19px',
              color: '#cfd3dc',
              align: 'center',
              wordWrap: { width: dw - 60 },
            })
            .setOrigin(0.5)
            .setDepth(D + 3)
            .setName('hub-log-description'),
        );
      }
      this.logTimer = { entry, countdown, status, last: '' };
    };
    select(UPDATES[0]);
  }

  /** Every frame while the Update Log is open: the countdown, and the status flip. */
  private tickLog(): void {
    const t = this.logTimer;
    if (!t) return;
    const now = Date.now();
    const line = updateCountdown(t.entry, now);
    if (line === t.last) return;
    t.last = line;
    t.countdown.setText(line);
    const status = updateStatus(t.entry, now);
    if (t.status.text !== status) {
      t.status.setText(status);
      t.status.setColor(status === 'RELEASED' ? '#7ed67e' : GOLD_HEX);
    }
  }

  // ── QUESTS ───────────────────────────────────────────────────────────────

  private buildQuests(body: Phaser.GameObjects.Container): void {
    const now = Date.now();
    this.questIndex = periodIndex(this.questPeriod, now);
    body.add(
      this.add
        .text(640, 158, 'QUESTS', { fontSize: '30px', color: QUEST_ORANGE_HEX, fontStyle: 'bold' })
        .setOrigin(0.5)
        .setLetterSpacing(3)
        .setName('hub-quests-title'),
    );
    const pill = (x: number, period: QuestPeriod, label: string) => {
      const on = this.questPeriod === period;
      const bg = this.add
        .rectangle(x, 206, 170, 40, on ? QUEST_ORANGE : 0x262234)
        .setStrokeStyle(2, on ? 0xffd9a8 : 0x3a3f4b)
        .setInteractive({ useHandCursor: true })
        .setName(`hub-quest-${period}`);
      const txt = this.add
        .text(x, 206, label, {
          fontSize: '17px',
          color: on ? '#1a140a' : '#cfd3dc',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setLetterSpacing(2);
      bg.on('pointerup', () => {
        if (this.busy || this.questPeriod === period) return;
        this.questPeriod = period;
        this.renderTab();
      });
      body.add(bg);
      body.add(txt);
    };
    pill(540, 'daily', 'DAILY');
    pill(740, 'weekly', 'WEEKLY');

    let rows: QuestRow[] = [];
    try {
      rows = quests().rows(this.questPeriod, now);
    } catch (err) {
      console.warn('[HubScene] quests unavailable', err);
    }
    rows.forEach((row, i) => {
      if (i < QUEST_ROW_YS.length) this.makeQuestRow(640, QUEST_ROW_YS[i], i, row, body);
    });

    this.questCountdown = this.add
      .text(640, 600, '', {
        fontSize: '18px',
        color: QUEST_ORANGE_HEX,
        fontStyle: 'bold',
        fontFamily: 'monospace',
      })
      .setOrigin(0.5)
      .setName('hub-quest-countdown');
    body.add(this.questCountdown);
    this.lastQuestLabel = '';
    this.tickQuests();
  }

  private lastQuestLabel = '';
  /** Once a second: the reset countdown; on a rollover, fresh quests (bars back). */
  private tickQuests(): void {
    if (!this.questCountdown) return;
    const now = Date.now();
    if (periodIndex(this.questPeriod, now) !== this.questIndex && !this.busy) {
      this.renderTab();
      return;
    }
    const left = periodEndsAt(this.questPeriod, now) - now;
    const label = `NEW QUESTS IN ${formatQuestCountdown(left)}`;
    if (label !== this.lastQuestLabel) {
      this.lastQuestLabel = label;
      this.questCountdown.setText(label);
    }
  }

  private makeQuestRow(
    cx: number,
    y: number,
    i: number,
    row: QuestRow,
    body: Phaser.GameObjects.Container,
  ): void {
    const left = cx - ROW_W / 2;
    const right = cx + ROW_W / 2;
    const reward = QUEST_REWARD[this.questPeriod];

    const panel = this.add.graphics();
    panel.fillStyle(0x15131f, 0.85);
    panel.fillRoundedRect(left, y - QUEST_ROW_H / 2, ROW_W, QUEST_ROW_H, 12);
    panel.lineStyle(
      row.claimed ? 2 : 1,
      row.claimed ? QUEST_GREEN : QUEST_ORANGE,
      row.claimed ? 0.9 : 0.5,
    );
    panel.strokeRoundedRect(left, y - QUEST_ROW_H / 2, ROW_W, QUEST_ROW_H, 12);
    body.add(panel);

    body.add(
      this.add
        .text(left + 30, y - 24, row.def.name, {
          fontSize: '22px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setName(`hub-quest-${i}-name`),
    );

    // The reward, beside the bar: coins and EXP.
    body.add(this.add.image(right - 250, y + 14, 'coin').setDisplaySize(26, 26));
    body.add(
      this.add
        .text(right - 228, y + 14, `+${reward.coins}`, {
          fontSize: '20px',
          color: GOLD_HEX,
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5),
    );
    body.add(
      this.add
        .text(right - 130, y + 14, `+${reward.exp} EXP`, {
          fontSize: '20px',
          color: '#9fd8ff',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5),
    );

    if (row.claimed) {
      body.add(
        this.add
          .text(left + 105, y + 14, 'CLAIMED!', {
            fontSize: '24px',
            color: '#7ed67e',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setName(`hub-quest-${i}-claimed`),
      );
      return;
    }

    if (row.complete) {
      // The green CLAIM button takes the bar's place.
      const btn = this.add
        .rectangle(left + 105, y + 14, 150, 44, QUEST_GREEN)
        .setStrokeStyle(2, 0xa5e8a5)
        .setInteractive({ useHandCursor: true })
        .setDepth(2)
        .setName(`hub-quest-${i}-claim`);
      body.add(btn);
      body.add(
        this.add
          .text(left + 105, y + 14, 'CLAIM', {
            fontSize: '20px',
            color: '#0d1f0d',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(3),
      );
      btn.on('pointerover', () => btn.setFillStyle(0x66d466));
      btn.on('pointerout', () => btn.setFillStyle(QUEST_GREEN));
      btn.on('pointerup', () => {
        if (this.busy) return;
        try {
          const paid = quests().claim(this.questPeriod, i, Date.now());
          if (!paid) return;
          this.balanceText.setText(coinLabel(this.progress.coins()));
          // The coins can cross Richness / Mansion.
          sweepAndToastAchievements(saveStorage());
        } catch (err) {
          console.warn('[HubScene] quest claim failed', err);
          return;
        }
        this.renderTab();
      });
      return;
    }

    // Progress bar: a named track, a proportional green fill, the count on top.
    const barX = left + 30;
    body.add(
      this.add
        .rectangle(barX, y + 14, QUEST_BAR_W, 18, 0x262234)
        .setOrigin(0, 0.5)
        .setStrokeStyle(1, 0x3a3f4b)
        .setName(`hub-quest-${i}-bar`),
    );
    const frac = row.def.goal > 0 ? row.progress / row.def.goal : 0;
    if (frac > 0) {
      body.add(
        this.add
          .rectangle(barX, y + 14, Math.max(4, Math.round(QUEST_BAR_W * frac)), 18, QUEST_GREEN)
          .setOrigin(0, 0.5),
      );
    }
    body.add(
      this.add
        .text(barX + QUEST_BAR_W / 2, y + 14, `${row.progress} / ${row.def.goal}`, {
          fontSize: '14px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(2),
    );
  }

  // ── POTIONS ──────────────────────────────────────────────────────────────

  private buildPotions(body: Phaser.GameObjects.Container): void {
    const now = Date.now();
    const epoch = potionEpoch();
    const cycle = cycleIndex(now, epoch);
    this.shownCycle = cycle;
    const stock = rollStock(cycle, epoch);
    const sold = soldFor(this.progress.potionShop(), cycle);

    this.countdown = this.add
      .text(640, 152, '', { fontSize: '18px', color: '#7ed67e', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setName('hub-potion-countdown');
    body.add(this.countdown);
    this.tickCountdown();

    stock.forEach((kind, i) => {
      if (i >= POTION_SLOTS) return;
      this.makePotionRow(640, POTION_ROW_YS[i], i, kind, sold[i], body);
    });
  }

  /** Once a second: update the countdown; on a rollover, redraw the stock. */
  override update(): void {
    if (this.logTimer) this.tickLog();
    if (this.tab === 'quests' && this.questCountdown) this.tickQuests();
    if (this.tab !== 'potions' || !this.countdown) return;
    this.tickCountdown();
  }

  private lastShown = '';
  private tickCountdown(): void {
    if (!this.countdown) return;
    const now = Date.now();
    const epoch = potionEpoch();
    if (cycleIndex(now, epoch) !== this.shownCycle && !this.busy) {
      this.renderTab(); // restocked while looking at the shelf (or a /restock)
      return;
    }
    const label = `STOCK RESETS IN ${formatRemaining(cycleEndsAt(now, epoch) - now)}`;
    if (label !== this.lastShown) {
      this.lastShown = label;
      this.countdown.setText(label);
    }
  }

  private makePotionRow(
    cx: number,
    y: number,
    slot: number,
    kind: PotionKind,
    isSold: boolean,
    body: Phaser.GameObjects.Container,
  ): void {
    const left = cx - ROW_W / 2;
    const right = cx + ROW_W / 2;
    const def = POTIONS[kind];
    const affordable = this.progress.coins() >= def.cost;

    const panel = this.add.graphics();
    panel.fillStyle(0x15131f, 0.85);
    panel.fillRoundedRect(left, y - POTION_ROW_H / 2, ROW_W, POTION_ROW_H, 12);
    panel.lineStyle(isSold ? 2 : 1, isSold ? HUB_RED : GOLD, isSold ? 0.9 : 0.5);
    panel.strokeRoundedRect(left, y - POTION_ROW_H / 2, ROW_W, POTION_ROW_H, 12);
    body.add(panel);

    if (this.textures.exists(def.art)) {
      body.add(this.add.image(left + 70, y, def.art).setDisplaySize(90, 90));
    }
    body.add(
      this.add
        .text(left + 130, y - 34, def.name, {
          fontSize: '22px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setName(`hub-potion-${slot}-name`),
    );
    body.add(
      this.add
        .text(left + 130, y, def.effect, { fontSize: '15px', color: '#9fd8ff' })
        .setOrigin(0, 0.5),
    );
    body.add(
      this.add
        .text(
          left + 130,
          y + 30,
          `Lasts ${formatRemaining(def.durationMs)} from the moment you use it.`,
          {
            fontSize: '13px',
            color: '#9aa6ad',
          },
        )
        .setOrigin(0, 0.5),
    );

    if (isSold) {
      body.add(
        this.add
          .text(right - 105, y, 'SOLD', { fontSize: '26px', color: HUB_RED_HEX, fontStyle: 'bold' })
          .setOrigin(0.5)
          .setName(`hub-potion-${slot}-sold`),
      );
      return;
    }

    body.add(this.add.image(right - 250, y, 'coin').setDisplaySize(30, 30));
    body.add(
      this.add
        .text(right - 228, y, `${def.cost}`, {
          fontSize: '22px',
          color: affordable ? GOLD_HEX : '#7f8c8d',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5),
    );
    const btn = this.add
      .rectangle(right - 105, y, 150, 52, affordable ? GOLD : 0x2a2a3a)
      .setStrokeStyle(2, affordable ? 0xfff3b0 : 0x555a66)
      .setInteractive({ useHandCursor: affordable })
      .setDepth(2)
      .setName(`hub-potion-${slot}-buy`);
    body.add(btn);
    body.add(
      this.add
        .text(right - 105, y, 'BUY', {
          fontSize: '20px',
          color: affordable ? '#1a140a' : '#7f8c8d',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(3),
    );
    if (affordable) {
      btn.on('pointerover', () => btn.setFillStyle(0xffd83a));
      btn.on('pointerout', () => btn.setFillStyle(GOLD));
    }
    btn.on('pointerup', () => {
      if (this.busy) return;
      if (!affordable) {
        flashHint(this, 'Not enough coins — win episodes to earn more.');
        return;
      }
      this.confirmPurchase(slot, kind);
    });
  }

  /** BUY → confirm → deduct, mark SOLD, then USE / STORE. */
  private confirmPurchase(slot: number, kind: PotionKind): void {
    const def = POTIONS[kind];
    const W = 1280;
    const H = 720;
    const D = 100;
    this.busy = true;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      return o;
    };
    add(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.7)
        .setInteractive()
        .setDepth(D),
    );
    add(
      this.add
        .rectangle(W / 2, H / 2, 560, 260, 0x1a1726, 1)
        .setStrokeStyle(2, GOLD)
        .setDepth(D + 1),
    );
    add(
      this.add
        .text(W / 2, H / 2 - 76, `Buy ${def.name}?`, {
          fontSize: '28px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    add(
      this.add
        .text(W / 2, H / 2 - 22, `${def.cost} Hambi coins.\n${def.effect}.`, {
          fontSize: '16px',
          color: '#9aa6ad',
          align: 'center',
          lineSpacing: 4,
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    const teardown = () => {
      objs.forEach((o) => o.destroy());
      this.busy = false;
    };
    const confirmBg = add(
      this.add
        .rectangle(W / 2 - 120, H / 2 + 70, 200, 52, 0x1a6b1a)
        .setStrokeStyle(2, 0x4caf50)
        .setInteractive({ useHandCursor: true })
        .setDepth(D + 2)
        .setName('hub-potion-confirm'),
    );
    add(
      this.add
        .text(W / 2 - 120, H / 2 + 70, 'BUY', {
          fontSize: '20px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    confirmBg.on('pointerup', () => {
      const cycle = cycleIndex(Date.now(), potionEpoch());
      // The stock may have rolled over under the dialog — then this slot no
      // longer holds this potion, so the sale is off and the shelf redraws.
      if (cycle !== this.shownCycle) {
        teardown();
        this.renderTab();
        return;
      }
      if (!this.progress.spendCoins(def.cost)) {
        teardown();
        flashHint(this, 'Not enough coins — win episodes to earn more.');
        return;
      }
      this.progress.markPotionSold(cycle, slot);
      recordStat('potionsBought'); // Quests: a potion bought is also an item obtained
      recordStat('obtains');
      this.award('scientist');
      this.balanceText.setText(coinLabel(this.progress.coins()));
      objs.forEach((o) => o.destroy());
      this.renderTab();
      this.offerUseOrStore(kind);
    });
    const cancelBg = add(
      this.add
        .rectangle(W / 2 + 120, H / 2 + 70, 200, 52, 0x2a2a3a)
        .setStrokeStyle(2, 0x6666aa)
        .setInteractive({ useHandCursor: true })
        .setDepth(D + 2)
        .setName('hub-potion-cancel'),
    );
    add(
      this.add
        .text(W / 2 + 120, H / 2 + 70, 'CANCEL', {
          fontSize: '20px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    cancelBg.on('pointerup', teardown);
  }

  /** The bought potion is in hand: use it now, or shelve it in Storage. */
  private offerUseOrStore(kind: PotionKind): void {
    const def = POTIONS[kind];
    this.busy = true;
    showUseDialog(this, {
      title: def.name,
      subtitle: def.targeted
        ? 'Use it now and pick the tower it goes on, or store it for later.'
        : `${def.effect}. Use it now, or store it for later.`,
      accent: 0x4caf50,
      art: def.art,
      secondLabel: 'STORE',
      useName: 'hub-potion-use',
      secondName: 'hub-potion-store',
      onUse: (close) => {
        close();
        this.busy = false;
        const r = usePotion(this, kind, 'HubScene', { tab: 'potions' });
        if (r.outcome === 'started') flashHint(this, `${def.name} is active!`, '#7ed67e');
        else if (r.outcome === 'refused') {
          flashHint(this, `${r.reason ?? 'Already active'} — it went to Storage instead.`);
        }
      },
      onSecond: (close) => {
        close();
        this.busy = false;
        this.progress.storePotion(kind);
        flashHint(this, `${def.name} is waiting in STORAGE.`, '#9fd8ff');
      },
    });
  }
}
